import { create } from "zustand";

interface UploadProgress {
  uploadId: string;
  filename: string;
  fileSize: number;
  uploadedChunks: number;
  totalChunks: number;
  status: "uploading" | "merging" | "completed" | "error";
  errorMessage?: string;
  startedAt: number;
  lastUpdateAt: number;
  uploadFinished?: boolean; // Показывать ли кнопки "Посмотреть результат"
  dismissed?: boolean; // Флаг для скрытия прогресс-бара (не удаляем из стора)
}

interface UploadState {
  activeUploads: Map<string, UploadProgress>;
  currentFile: File | null; // Текущий выбранный файл для загрузки
  estimatedMinutes: number | null; // Расчетное количество минут для текущего файла

  // URL upload state
  videoUrl: string | null; // Ссылка на видео (для загрузки по URL)
  uploadMethod: "file" | "url" | null; // Метод загрузки
  urlTranscriptionStarted: boolean; // Флаг успешной отправки URL на транскрипцию
  isLoadingMetadata: boolean; // Загрузка метаданных видео

  // Actions
  setCurrentFile: (file: File | null) => void;
  setEstimatedMinutes: (minutes: number | null) => void;
  setVideoUrl: (url: string | null) => void;
  setUploadMethod: (method: "file" | "url" | null) => void;
  setUrlTranscriptionStarted: (started: boolean) => void;
  setIsLoadingMetadata: (loading: boolean) => void;
  startUpload: (uploadId: string, filename: string, fileSize: number, totalChunks: number) => void;
  updateProgress: (uploadId: string, uploadedChunks: number) => void;
  setStatus: (uploadId: string, status: UploadProgress["status"], errorMessage?: string) => void;
  completeUpload: (uploadId: string) => void;
  setUploadFinished: (uploadId: string, finished: boolean) => void;
  setDismissed: (uploadId: string, dismissed: boolean) => void;
  removeUpload: (uploadId: string) => void;
  clearAll: () => void; // Очистить все (при клике "Загрузить новый файл")
  clearEstimatedMinutes: () => void; // Очистить только минуты (при начале транскрипции)
  getUpload: (uploadId: string) => UploadProgress | undefined;
}

export const useUploadStore = create<UploadState>()((set, get) => ({
  activeUploads: new Map(),
  currentFile: null,
  estimatedMinutes: null,
  videoUrl: null,
  uploadMethod: null,
  urlTranscriptionStarted: false,
  isLoadingMetadata: false,

  setCurrentFile: (file): void => {
    set({ currentFile: file });
  },

  setEstimatedMinutes: (minutes): void => {
    set({ estimatedMinutes: minutes });
  },

  setVideoUrl: (url): void => {
    set({ videoUrl: url });
  },

  setUploadMethod: (method): void => {
    set({ uploadMethod: method });
  },

  setUrlTranscriptionStarted: (started): void => {
    set({ urlTranscriptionStarted: started });
  },

  setIsLoadingMetadata: (loading): void => {
    set({ isLoadingMetadata: loading });
  },

  startUpload: (uploadId, filename, fileSize, totalChunks): void => {
    set(state => {
      const newUploads = new Map(state.activeUploads);
      const now = Date.now();
      newUploads.set(uploadId, {
        uploadId,
        filename,
        fileSize,
        uploadedChunks: 0,
        totalChunks,
        status: "uploading",
        startedAt: now,
        lastUpdateAt: now
      });
      return { activeUploads: newUploads };
    });
  },

  updateProgress: (uploadId, uploadedChunks): void => {
    set(state => {
      const upload = state.activeUploads.get(uploadId);
      if (!upload) {
        return state;
      }

      const newUploads = new Map(state.activeUploads);
      newUploads.set(uploadId, {
        ...upload,
        uploadedChunks,
        lastUpdateAt: Date.now()
      });
      return { activeUploads: newUploads };
    });
  },

  setStatus: (uploadId, status, errorMessage): void => {
    set(state => {
      const upload = state.activeUploads.get(uploadId);
      if (!upload) {
        return state;
      }

      const newUploads = new Map(state.activeUploads);
      newUploads.set(uploadId, {
        ...upload,
        status,
        errorMessage
      });
      return { activeUploads: newUploads };
    });
  },

  completeUpload: (uploadId): void => {
    set(state => {
      const newUploads = new Map(state.activeUploads);
      const upload = newUploads.get(uploadId);
      if (upload) {
        newUploads.set(uploadId, {
          ...upload,
          status: "completed",
          uploadedChunks: upload.totalChunks
        });
      }
      return { activeUploads: newUploads };
    });
  },

  setUploadFinished: (uploadId, finished): void => {
    set(state => {
      const upload = state.activeUploads.get(uploadId);
      if (!upload) {
        return state;
      }

      const newUploads = new Map(state.activeUploads);
      newUploads.set(uploadId, {
        ...upload,
        uploadFinished: finished
      });
      return { activeUploads: newUploads };
    });
  },

  setDismissed: (uploadId, dismissed): void => {
    set(state => {
      const upload = state.activeUploads.get(uploadId);
      if (!upload) {
        return state;
      }

      const newUploads = new Map(state.activeUploads);
      newUploads.set(uploadId, {
        ...upload,
        dismissed
      });
      return { activeUploads: newUploads };
    });
  },

  removeUpload: (uploadId): void => {
    set(state => {
      const newUploads = new Map(state.activeUploads);
      newUploads.delete(uploadId);
      return { activeUploads: newUploads };
    });
  },

  clearAll: (): void => {
    set({
      activeUploads: new Map(),
      currentFile: null,
      estimatedMinutes: null,
      videoUrl: null,
      uploadMethod: null,
      urlTranscriptionStarted: false,
      isLoadingMetadata: false
    });
  },

  clearEstimatedMinutes: (): void => {
    set({
      estimatedMinutes: null,
      isLoadingMetadata: false
    });
  },

  getUpload: (uploadId): UploadProgress | undefined => {
    return get().activeUploads.get(uploadId);
  }
}));
